import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { 
  Users,
  TrendingUp,
  Activity,
  Euro,
  Phone,
  MapPin,
  Mail
} from 'lucide-react';

export default function TeamPage() {
  const { api, user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const response = await api().get('/team');
      setTeam(response.data);
    } catch (error) {
      console.error('Failed to load team', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'gestor': return 'bg-blue-100 text-blue-800';
      case 'vendedor': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'gestor': return 'Gestor';
      case 'vendedor': return 'Vendedor';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  // Calculate team totals
  const totalOpportunities = team.reduce((sum, m) => sum + (m.stats?.opportunities || 0), 0);
  const totalWonValue = team.reduce((sum, m) => sum + (m.stats?.won_value || 0), 0);
  const totalActivities = team.reduce((sum, m) => sum + (m.stats?.activities || 0), 0);

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Membros</p>
                <p className="text-2xl font-bold font-[Manrope]">{team.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Oportunidades</p>
                <p className="text-2xl font-bold font-[Manrope]">{totalOpportunities}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Euro className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Valor Ganho</p>
                <p className="text-2xl font-bold font-[Manrope] tabular-nums">{formatCurrency(totalWonValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Activity className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Atividades</p>
                <p className="text-2xl font-bold font-[Manrope]">{totalActivities}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {team.map((member) => {
          const isCurrentUser = member.id === user?.id;
          
          return (
            <Card 
              key={member.id} 
              className={`hover:shadow-md transition-shadow ${isCurrentUser ? 'ring-2 ring-indigo-500' : ''}`}
              data-testid={`team-member-${member.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-14 w-14 border-2 border-slate-100">
                    <AvatarFallback className="bg-indigo-100 text-indigo-800 text-lg font-semibold">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold font-[Manrope] truncate">{member.name}</h3>
                      {isCurrentUser && (
                        <Badge variant="outline" className="text-xs">Você</Badge>
                      )}
                    </div>
                    <Badge className={`text-xs ${getRoleColor(member.role)}`}>
                      {getRoleLabel(member.role)}
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-1 truncate flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {member.email}
                    </p>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="mt-6 space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Oportunidades</span>
                      <span className="font-medium">{member.stats?.opportunities || 0}</span>
                    </div>
                    <Progress 
                      value={totalOpportunities > 0 ? ((member.stats?.opportunities || 0) / totalOpportunities) * 100 : 0} 
                      className="h-1.5"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold font-[Manrope] text-emerald-600 tabular-nums">
                        {formatCurrency(member.stats?.won_value)}
                      </p>
                      <p className="text-xs text-muted-foreground">Ganho</p>
                    </div>
                    <div className="text-center p-3 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold font-[Manrope] text-blue-600">
                        {member.stats?.activities || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Atividades</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {team.length === 0 && (
        <Card>
          <CardContent className="p-16 text-center">
            <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold font-[Manrope]">Equipa vazia</h3>
            <p className="text-muted-foreground mt-1">Convide membros para a sua equipa</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
